import React from 'react';
import { Edit as EditIcon, LocalHospital as DiagnosisIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

export interface Diagnosis {
  diagnosis_name: string;
  code?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  isActive: boolean;
}

interface PatientDiagnosesCardProps {
  diagnoses: Diagnosis[];
  isLoading?: boolean;
  onEdit?: () => void;
}

export const PatientDiagnosesCard: React.FC<PatientDiagnosesCardProps> = ({
  diagnoses,
  isLoading = false,
  onEdit,
}) => {
  const activeDiagnoses = diagnoses.filter(d => d.isActive);
  const inactiveDiagnoses = diagnoses.filter(d => !d.isActive);

  return (
    <Card
      sx={{
        width: '100%',
        minWidth: 400,
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <DiagnosisIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            Patient Diagnoses
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
            <Typography
              variant="caption"
              sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}
            >
              {diagnoses.length} total • {activeDiagnoses.length} active
            </Typography>
            {onEdit && (
              <Chip
                label="+ Add"
                size="small"
                onClick={onEdit}
                sx={{
                  height: 18,
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              />
            )}
          </Box>
        </Box>
        {onEdit && (
          <Tooltip title="Edit diagnoses">
            <IconButton
              size="small"
              onClick={onEdit}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent
        sx={{
          p: 1.5,
          pt: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          maxHeight: 400,
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading diagnoses...
            </Typography>
          </Box>
        ) : diagnoses.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No diagnoses recorded for this patient.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {/* Active Diagnoses */}
            {activeDiagnoses.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Active Diagnoses
                </Typography>
                <Stack spacing={1}>
                  {activeDiagnoses.map((diag, idx) => (
                    <Box
                      key={idx}
                      onClick={onEdit}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'backgroundColors.tertiary',
                        border: '1px solid',
                        borderColor: 'borderColors.lightprimary',
                        cursor: onEdit ? 'pointer' : 'default',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': onEdit
                          ? {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                            }
                          : {},
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: 'textColors.darkText',
                              mb: 0.25,
                            }}
                          >
                            {diag.diagnosis_name}
                          </Typography>
                          {diag.code && (
                            <Typography
                              variant="caption"
                              sx={{ fontSize: '0.6875rem', color: 'textColors.lightText' }}
                            >
                              Code: {diag.code}
                            </Typography>
                          )}
                          {diag.start_date && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6875rem',
                                color: 'textColors.lightText',
                                mt: 0.25,
                                display: 'block',
                              }}
                            >
                              Started: {new Date(diag.start_date).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label="Active"
                          size="small"
                          color="success"
                          sx={{ height: 18, fontSize: '0.625rem' }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Inactive Diagnoses */}
            {inactiveDiagnoses.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Inactive Diagnoses
                </Typography>
                <Stack spacing={1}>
                  {inactiveDiagnoses.slice(0, 3).map((diag, idx) => (
                    <Box
                      key={idx}
                      onClick={onEdit}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'backgroundColors.tertiary',
                        border: '1px solid',
                        borderColor: 'borderColors.lightprimary',
                        opacity: 0.7,
                        cursor: onEdit ? 'pointer' : 'default',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': onEdit
                          ? {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                              opacity: 1,
                            }
                          : {},
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: 'textColors.darkText',
                            }}
                          >
                            {diag.diagnosis_name}
                          </Typography>
                          {diag.end_date && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6875rem',
                                color: 'textColors.lightText',
                                mt: 0.25,
                                display: 'block',
                              }}
                            >
                              Ended: {new Date(diag.end_date).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label="Inactive"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.625rem',
                            bgcolor: 'backgroundColors.secondary',
                          }}
                        />
                      </Stack>
                    </Box>
                  ))}
                  {inactiveDiagnoses.length > 3 && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.6875rem', color: 'textColors.lightText', pl: 1 }}
                    >
                      ...and {inactiveDiagnoses.length - 3} more
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
